import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Mail, 
  Reply, 
  TrendingUp, 
  Calendar, 
  Users, 
  Activity,
  RefreshCw,
  Clock,
  CheckCircle,
  AlertCircle,
  MessageSquare
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import EmailSendsTracker from './EmailSendsTracker';
import { EmailSendsDetailView } from './EmailSendsDetailView';

// ROLLBACK: Use obsync_dynamic_reports only
async function queryObsyncReports(
  selectClause: string,
  eventType: string,
  filters: { [key: string]: any } = {},
  options: { count?: boolean; limit?: number; order?: { column: string; ascending: boolean } } = {}
): Promise<{ data: any[] | null; count?: number | null; error: any }> {
  const obsyncTable = 'obsync_dynamic_reports';
  
  let fallbackQuery = supabase.from(obsyncTable).select(selectClause, options.count ? { count: 'exact', head: options.count && !selectClause } : {});
  
  fallbackQuery = fallbackQuery.eq('event_type', eventType);
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (key === 'gte' || key === 'lte' || key === 'gt' || key === 'lt') {
        const [field, filterVal] = Object.entries(value)[0];
        fallbackQuery = (fallbackQuery as any)[key](field, filterVal);
      } else if (key === 'or') {
        fallbackQuery = fallbackQuery.or(value);
      } else if (key === 'not') {
        const [field, op, val] = value;
        fallbackQuery = fallbackQuery.not(field, op, val);
      } else {
        fallbackQuery = fallbackQuery.eq(key, value);
      }
    }
  });
  
  if (options.order) {
    fallbackQuery = fallbackQuery.order(options.order.column, { ascending: options.order.ascending });
  }
  
  if (options.limit) {
    fallbackQuery = fallbackQuery.limit(options.limit);
  }
  
  const { data, error, count } = await fallbackQuery;
  
  if (error) {
    console.error(`Error from ${obsyncTable}:`, error.message);
    return { data: null, count: null, error };
  }
  
  console.log(`Successfully fetched from ${obsyncTable}:`, { eventType, count: count || data?.length });
  return { data, count, error: null };
}

interface LiveFeedMetrics {
  // Email Sends
  totalSends: number;
  uniqueSends: number;
  sendsToday: number;
  sendsYesterday: number;
  sendsThisWeek: number;
  
  // Replies
  totalReplies: number;
  humanReplies: number;
  uniqueReplies: number;
  repliesToday: number;
  repliesYesterday: number;
  repliesThisWeek: number;
  followUpReplies: number;
  
  // Categorization
  categorizedReplies: number;
  positiveReplies: number;
  positiveRepliesToday: number;
  positiveRepliesYesterday: number;
  negativeReplies: number;
  neutralReplies: number;
  
  // Meeting Bookings
  meetingsBooked: number;
  meetingsToday: number;
  meetingsYesterday: number;
  
  // Real-time
  lastUpdate: string;
  isLive: boolean;
}

interface RecentActivity {
  id: string;
  type: 'send' | 'reply' | 'meeting';
  timestamp: string;
  email: string;
  subject: string;
  category?: string;
  sentiment?: string;
  campaignName?: string;
}

export default function LiveFeedDashboard() {
  const [metrics, setMetrics] = useState<LiveFeedMetrics>({
    totalSends: 0,
    uniqueSends: 0,
    sendsToday: 0,
    sendsYesterday: 0,
    sendsThisWeek: 0,
    totalReplies: 0,
    humanReplies: 0,
    uniqueReplies: 0,
    repliesToday: 0,
    repliesYesterday: 0,
    repliesThisWeek: 0,
    followUpReplies: 0,
    categorizedReplies: 0,
    positiveReplies: 0,
    positiveRepliesToday: 0,
    positiveRepliesYesterday: 0,
    negativeReplies: 0,
    neutralReplies: 0,
    meetingsBooked: 0,
    meetingsToday: 0,
    meetingsYesterday: 0,
    lastUpdate: new Date().toISOString(),
    isLive: true
  });

  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastMondayRefresh, setLastMondayRefresh] = useState<string | null>(null);
  const [showEmailSendsDetail, setShowEmailSendsDetail] = useState(false);
  const { toast } = useToast();

  const fetchMetrics = async () => {
    try {
      console.log('🚀 [LiveFeedDashboard] Starting metrics fetch...');
      setIsLoading(true);

      // Get current time boundaries using UTC to match database
      const now = new Date();
      const todayStart = new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
      const monthStart = new Date(now.getUTCFullYear(), now.getUTCMonth(), 1); // First day of current month
      const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      console.log('🔍 [LiveFeedDashboard] Fetching metrics with UTC boundaries:', {
        todayStart: todayStart.toISOString(),
        monthStart: monthStart.toISOString(),
        weekStart: weekStart.toISOString()
      });

      // [PHASE 3 DAY 9] Fetch email sends with dual-read strategy
      const { data: sendsData, error: sendsError } = await queryObsyncReports(
        'id, created_at, from_email, to_email, campaign_name, event_type, client_id',
        'EMAIL_SENT',
        {},
        { order: { column: 'created_at', ascending: false } }
      );

      if (sendsError) {
        console.error(`❌ [LiveFeedDashboard] Error fetching sends data from ${sendsTable}:`, sendsError);
        throw sendsError;
      }
      
      console.log(`✅ [LiveFeedDashboard] Fetched ${sendsData?.length || 0} EMAIL_SENT events from ${sendsTable}`);

      // [PHASE 3 DAY 9] Fetch replies with dual-read strategy
      const { data: repliesData, error: repliesError } = await queryObsyncReports(
        'id, created_at, time_replied, response_category, reply_category_type, stats_thread_id, reply_count, to_email, from_email, campaign_name, client_id',
        'EMAIL_REPLY',
        {},
        { order: { column: 'created_at', ascending: false } }
      );
      
      console.log(`✅ [LiveFeedDashboard] Fetched ${repliesData?.length || 0} EMAIL_REPLY events from ${repliesTable}`);

      // [PHASE 3 DAY 9] Fetch weekly sends count with dual-read
      const { count: weeklySendsCount, error: weeklySendsError } = await queryObsyncReports(
        '*',
        'EMAIL_SENT',
        { gte: { created_at: weekStart.toISOString() } },
        { count: true }
      );

      if (weeklySendsError) {
        console.error(`❌ [LiveFeedDashboard] Error fetching weekly sends count from ${weeklySendsTable}:`, weeklySendsError);
      } else {
        console.log(`✅ [LiveFeedDashboard] Weekly sends count: ${weeklySendsCount} from ${weeklySendsTable}`);
      }

      // [PHASE 3 DAY 9] Fetch weekly sends sample with dual-read
      const { data: weeklySendsSample, error: weeklySendsSampleError } = await queryObsyncReports(
        'id, created_at, from_email, to_email, campaign_name, event_type, client_id',
        'EMAIL_SENT',
        { gte: { created_at: weekStart.toISOString() } },
        { order: { column: 'created_at', ascending: false }, limit: 5 }
      );

      if (weeklySendsSampleError) {
        console.error(`❌ [LiveFeedDashboard] Error fetching weekly sends sample from ${weeklySampleTable}:`, weeklySendsSampleError);
      } else {
        console.log(`✅ [LiveFeedDashboard] Weekly sends sample: ${weeklySendsSample?.length || 0} from ${weeklySampleTable}`);
      }

      // [PHASE 3 DAY 9] Fetch weekly replies data with dual-read
      const { data: weeklyRepliesData, error: weeklyRepliesError } = await queryObsyncReports(
        'id, created_at, time_replied, response_category, reply_category_type, stats_thread_id, reply_count, to_email, from_email, campaign_name, client_id',
        'EMAIL_REPLY',
        { gte: { created_at: weekStart.toISOString() } },
        { order: { column: 'created_at', ascending: false }, limit: 10000 }
      );

      if (weeklyRepliesError) {
        console.error(`❌ [LiveFeedDashboard] Error fetching weekly replies data from ${weeklyRepliesTable}:`, weeklyRepliesError);
      } else {
        console.log(`✅ [LiveFeedDashboard] Weekly replies: ${weeklyRepliesData?.length || 0} from ${weeklyRepliesTable}`);
      }

      // [PHASE 3 DAY 9] Fetch MTD sends count with dual-read
      const { count: mtdSendsCount, error: mtdSendsError } = await queryObsyncReports(
        '*',
        'EMAIL_SENT',
        { gte: { created_at: monthStart.toISOString() } },
        { count: true }
      );

      if (mtdSendsError) {
        console.error(`❌ [LiveFeedDashboard] Error fetching MTD sends count from ${mtdSendsTable}:`, mtdSendsError);
      } else {
        console.log(`✅ [LiveFeedDashboard] MTD sends count: ${mtdSendsCount} from ${mtdSendsTable}`);
      }

      // [PHASE 3 DAY 9] Fetch MTD replies count with dual-read
      const { count: mtdRepliesCount, error: mtdRepliesError } = await queryObsyncReports(
        '*',
        'EMAIL_REPLY',
        {
          gte: { created_at: monthStart.toISOString() },
          reply_count: 1,
          or: 'reply_category_type.is.null,reply_category_type.not.in.(auto-reply,OOO)'
        },
        { count: true }
      );

      if (mtdRepliesError) {
        console.error(`❌ [LiveFeedDashboard] Error fetching MTD replies count from ${mtdRepliesTable}:`, mtdRepliesError);
      } else {
        console.log(`✅ [LiveFeedDashboard] MTD replies count: ${mtdRepliesCount} from ${mtdRepliesTable}`);
      }

      // [PHASE 3 DAY 9] Fetch MTD positive replies count with dual-read
      const { count: mtdPositiveCount, error: mtdPositiveError } = await queryObsyncReports(
        '*',
        'EMAIL_REPLY',
        {
          gte: { created_at: monthStart.toISOString() },
          reply_count: 1,
          reply_category_type: 'positive'
        },
        { count: true }
      );

      if (mtdPositiveError) {
        console.error(`❌ [LiveFeedDashboard] Error fetching MTD positive replies count from ${mtdPositiveTable}:`, mtdPositiveError);
      } else {
        console.log(`✅ [LiveFeedDashboard] MTD positive count: ${mtdPositiveCount} from ${mtdPositiveTable}`);
      }

      // [PHASE 3 DAY 9] Fetch MTD categorized replies count with dual-read
      const { count: mtdCategorizedCount, error: mtdCategorizedError } = await queryObsyncReports(
        '*',
        'EMAIL_REPLY',
        {
          gte: { created_at: monthStart.toISOString() },
          reply_count: 1,
          not: ['reply_category_type', 'is', null]
        },
        { count: true }
      );

      if (mtdCategorizedError) {
        console.error(`❌ [LiveFeedDashboard] Error fetching MTD categorized replies count from ${mtdCategorizedTable}:`, mtdCategorizedError);
      } else {
        console.log(`✅ [LiveFeedDashboard] MTD categorized count: ${mtdCategorizedCount} from ${mtdCategorizedTable}`);
      }

      // Fetch MTD meetings count from lead_category_updates
      const { count: mtdMeetingsCount, error: mtdMeetingsError } = await supabase
        .from('lead_category_updates')
        .select('*', { count: 'exact', head: true })
        .in('category', ['Meeting booked', 'Meeting Request'])
        .gte('created_at', monthStart.toISOString());

      if (mtdMeetingsError) {
        console.error('❌ [LiveFeedDashboard] Error fetching MTD meetings count:', mtdMeetingsError);
      }

      // [PHASE 3 DAY 9] Fetch MTD unique sends data with dual-read
      const { data: mtdSendsData, error: mtdSendsDataError } = await queryObsyncReports(
        'from_email, to_email, campaign_name',
        'EMAIL_SENT',
        { gte: { created_at: monthStart.toISOString() } },
        { limit: 50000 }
      );

      if (mtdSendsDataError) {
        console.error(`❌ [LiveFeedDashboard] Error fetching MTD sends data from ${mtdSendsDataTable}:`, mtdSendsDataError);
      } else {
        console.log(`✅ [LiveFeedDashboard] MTD sends data: ${mtdSendsData?.length || 0} from ${mtdSendsDataTable}`);
      }

      console.log('🔍 [LiveFeedDashboard] MTD sends data debug:', {
        mtdSendsDataLength: mtdSendsData?.length || 0,
        mtdSendsCount: mtdSendsCount || 0,
        sampleData: mtdSendsData?.slice(0, 3)
      });

      // Fetch MTD unique threads count
      // [PHASE 3 DAY 9] Fetch MTD replies data (first replies only) with dual-read
      const { data: mtdRepliesData, error: mtdRepliesDataError2 } = await queryObsyncReports(
        'to_email, from_email, reply_count',
        'EMAIL_REPLY',
        {
          gte: { created_at: monthStart.toISOString() },
          reply_count: 1,
          or: 'reply_category_type.is.null,reply_category_type.not.in.(auto-reply,OOO)'
        },
        {}
      );

      if (mtdRepliesDataError) {
        console.error(`❌ [LiveFeedDashboard] Error fetching MTD replies data from ${mtdRepliesDataTable2}:`, mtdRepliesDataError);
      } else {
        console.log(`✅ [LiveFeedDashboard] MTD first replies: ${mtdRepliesData?.length || 0} from ${mtdRepliesDataTable2}`);
      }

      // [PHASE 3 DAY 9] Fetch all MTD replies data (all replies, not just first) with dual-read
      const { data: allMtdRepliesData, error: allMtdRepliesDataError } = await queryObsyncReports(
        'to_email, from_email, reply_count',
        'EMAIL_REPLY',
        {
          gte: { created_at: monthStart.toISOString() },
          or: 'reply_category_type.is.null,reply_category_type.not.in.(auto-reply,OOO)'
        },
        {}
      );

      if (allMtdRepliesDataError) {
        console.error(`❌ [LiveFeedDashboard] Error fetching all MTD replies data from ${allMtdRepliesTable}:`, allMtdRepliesDataError);
      } else {
        console.log(`✅ [LiveFeedDashboard] All MTD replies: ${allMtdRepliesData?.length || 0} from ${allMtdRepliesTable}`);
      }

      if (repliesError) {
        console.error('❌ [LiveFeedDashboard] Error fetching replies data:', repliesError);
        throw repliesError;
      }

        console.log('📊 [LiveFeedDashboard] Data fetched (using created_at + first replies only):', {
          sendsCount: sendsData?.length || 0,
          repliesCount: repliesData?.length || 0,
          weeklySendsCount: weeklySendsCount || 0,
          weeklyRepliesCount: weeklyRepliesData?.length || 0,
          mtdSendsCount: mtdSendsCount || 0,
          mtdRepliesCount: mtdRepliesCount || 0,
          mtdPositiveCount: mtdPositiveCount || 0,
          mtdCategorizedCount: mtdCategorizedCount || 0,
          mtdMeetingsCount: mtdMeetingsCount || 0,
          mtdSendsDataCount: mtdSendsData?.length || 0,
          mtdRepliesDataCount: mtdRepliesData?.length || 0,
          allMtdRepliesDataCount: allMtdRepliesData?.length || 0,
          weekStart: weekStart.toISOString(),
          monthStart: monthStart.toISOString(),
          weekEnd: new Date().toISOString(),
          note: 'Now using created_at for date filtering, only counting first replies, and including NULL reply_category_type values to match Client Dashboard logic exactly'
        });

      // Debug: Show sample of weekly data
      if (weeklySendsSample && weeklySendsSample.length > 0) {
        console.log('📧 [LiveFeedDashboard] Sample weekly sends:', weeklySendsSample.slice(0, 3));
      }
      if (weeklyRepliesData && weeklyRepliesData.length > 0) {
        console.log('📨 [LiveFeedDashboard] Sample weekly replies:', weeklyRepliesData.slice(0, 3));
      }

      // Process sends data
        const totalSends = mtdSendsCount || 0; // Use MTD data for main card
        const uniqueSends = new Set(mtdSendsData?.map(s => `${s.from_email}-${s.to_email}-${s.campaign_name}`)).size; // Use MTD data
        
        console.log('🔍 [LiveFeedDashboard] Unique sends calculation debug:', {
          mtdSendsDataLength: mtdSendsData?.length || 0,
          uniqueSends,
          totalSends,
          sampleUniqueKeys: Array.from(new Set(mtdSendsData?.map(s => `${s.from_email}-${s.to_email}-${s.campaign_name}`))).slice(0, 5)
        });
      const sendsToday = sendsData?.filter(s => new Date(s.created_at) >= todayStart).length || 0;
        const sendsYesterday = sendsData?.filter(s => {
          const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);
          const yesterdayEnd = new Date(todayStart);
          const sendDate = new Date(s.created_at);
          return sendDate >= yesterdayStart && sendDate < yesterdayEnd;
        }).length || 0;
        const sendsThisWeek = weeklySendsCount || 0; // Use count directly

        // [PHASE 3 DAY 9] Get total replies (all first replies) for main card display with dual-read
        const { count: mtdTotalRepliesCount, error: mtdTotalRepliesError } = await queryObsyncReports(
          '*',
          'EMAIL_REPLY',
          {
            gte: { created_at: monthStart.toISOString() },
            reply_count: 1
          },
          { count: true }
        );

        if (mtdTotalRepliesError) {
          console.error(`❌ [LiveFeedDashboard] Error fetching MTD total replies count from ${mtdTotalRepliesTable}:`, mtdTotalRepliesError);
        } else {
          console.log(`✅ [LiveFeedDashboard] MTD total replies count: ${mtdTotalRepliesCount} from ${mtdTotalRepliesTable}`);
        }

        const totalReplies = mtdTotalRepliesCount || 0; // Use total replies for main card
        const humanReplies = mtdRepliesCount || 0; // Use human replies for sub-metric
        
        // Filter out auto-reply and OOO for human replies (from all data for today's calculations)
        // Use created_at for date filtering and only count first replies to match Client Dashboard logic
        // Include NULL reply_category_type values as per RPC function logic
        const humanRepliesData = repliesData?.filter(r => 
          r.reply_count === 1 && (r.reply_category_type === null || !['auto-reply', 'OOO'].includes(r.reply_category_type))
        ) || [];
        
        // Filter out auto-reply and OOO for weekly human replies (only first replies)
        const weeklyHumanReplies = weeklyRepliesData?.filter(r => 
          r.reply_count === 1 && (r.reply_category_type === null || !['auto-reply', 'OOO'].includes(r.reply_category_type))
        ) || [];
        
        const uniqueReplies = new Set(mtdRepliesData?.filter(r => r.reply_count === 1).map(r => `${r.to_email}-${r.from_email}`)).size; // Use MTD data
        const repliesToday = humanRepliesData?.filter(r => new Date(r.created_at) >= todayStart).length || 0;
        const repliesYesterday = humanRepliesData?.filter(r => {
          const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);
          const yesterdayEnd = new Date(todayStart);
          const replyDate = new Date(r.created_at);
          return replyDate >= yesterdayStart && replyDate < yesterdayEnd;
        }).length || 0;
        const repliesThisWeek = weeklyHumanReplies?.length || 0; // Use weekly data directly
      
      // Follow-up replies: reply_count > 1, excluding auto-reply and OOO (from all MTD data, not just first replies)
      const followUpReplies = allMtdRepliesData?.filter(r => r.reply_count && r.reply_count > 1).length || 0;
      
      // Unique threads: reply_count = 1, excluding auto-reply and OOO (from MTD data)
      // Use combination of to_email and from_email for first replies since stats_thread_id is null
      const uniqueThreads = new Set(
        mtdRepliesData?.filter(r => r.reply_count === 1).map(r => `${r.to_email}-${r.from_email}`)
      ).size;

      // Process categorization using correct field names (MTD data)
      const categorizedReplies = mtdCategorizedCount || 0; // Use MTD data
      
        const positiveReplies = mtdPositiveCount || 0; // Use MTD data for main card
        
        // Calculate positive replies for today and yesterday
        const positiveRepliesToday = repliesData?.filter(r => 
          r.reply_count === 1 && 
          r.reply_category_type === 'positive' && 
          new Date(r.created_at) >= todayStart
        ).length || 0;
        
        const positiveRepliesYesterday = repliesData?.filter(r => {
          const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);
          const yesterdayEnd = new Date(todayStart);
          const replyDate = new Date(r.created_at);
          return r.reply_count === 1 && 
                 r.reply_category_type === 'positive' && 
                 replyDate >= yesterdayStart && 
                 replyDate < yesterdayEnd;
        }).length || 0;
        
        const negativeReplies = weeklyRepliesData?.filter(r => 
          r.reply_category_type === 'negative' && 
          new Date(r.created_at) >= weekStart
      ).length || 0;
        
        const neutralReplies = weeklyRepliesData?.filter(r => 
          r.reply_category_type === 'neutral' && 
          new Date(r.created_at) >= weekStart
      ).length || 0;

        // Fetch meetings from lead_category_updates table (this week)
        const { data: meetingsData, error: meetingsError } = await supabase
          .from('lead_category_updates')
          .select('id, created_at, category')
          .in('category', ['Meeting booked', 'Meeting Request'])
          .gte('created_at', weekStart.toISOString());

        if (meetingsError) {
          console.error('❌ [LiveFeedDashboard] Error fetching meetings data:', meetingsError);
        }

        const meetingsBooked = mtdMeetingsCount || 0; // Use MTD data for main card
        const meetingsToday = meetingsData?.filter(m => new Date(m.created_at) >= todayStart).length || 0;
        const meetingsYesterday = meetingsData?.filter(m => {
          const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);
          const yesterdayEnd = new Date(todayStart);
          const meetingDate = new Date(m.created_at);
          return meetingDate >= yesterdayStart && meetingDate < yesterdayEnd;
        }).length || 0;

      console.log('📊 [LiveFeedDashboard] Processed metrics:', {
        totalSends,
        uniqueSends,
        sendsToday,
        sendsYesterday,
        sendsThisWeek,
        totalReplies,
        humanReplies,
        uniqueReplies,
        repliesToday,
        repliesYesterday,
        repliesThisWeek,
        followUpReplies,
        uniqueThreads,
        categorizedReplies,
        positiveReplies,
        positiveRepliesToday,
        positiveRepliesYesterday,
        negativeReplies,
        neutralReplies,
        meetingsBooked,
        meetingsToday,
        meetingsYesterday
      });

      // Debug MTD Activity calculations
      console.log('🔍 [LiveFeedDashboard] MTD Activity Debug:', {
        mtdSends: totalSends,
        mtdReplies: totalReplies,
        mtdPositive: positiveReplies,
        mtdMeetings: meetingsBooked,
        sendsToday: sendsToday,
        repliesToday: repliesToday,
        meetingsToday: meetingsToday,
        monthStart: monthStart.toISOString(),
        monthEnd: new Date().toISOString()
      });

      // Debug Weekly Activity calculations
      console.log('🔍 [LiveFeedDashboard] Weekly Activity Debug:', {
        sendsThisWeek: sendsThisWeek,
        repliesThisWeek: repliesThisWeek,
        followUpReplies: followUpReplies,
        uniqueThreads: uniqueThreads,
        weeklyHumanRepliesCount: weeklyHumanReplies?.length || 0,
        weeklyFirstRepliesCount: weeklyHumanReplies?.filter(r => r.reply_count === 1).length || 0,
        weeklyFollowUpRepliesCount: weeklyHumanReplies?.filter(r => r.reply_count && r.reply_count > 1).length || 0,
        weekStart: weekStart.toISOString(),
        weekEnd: new Date().toISOString()
      });

      // Additional debugging for data verification
      console.log('🔍 [LiveFeedDashboard] Data Verification:', {
        weeklySendsSample: weeklySendsSample?.slice(0, 3), // Show first 3 records
        weeklyRepliesData: weeklyRepliesData?.slice(0, 3), // Show first 3 records
        weeklyHumanReplies: weeklyHumanReplies?.slice(0, 3), // Show first 3 records
        totalWeeklySends: weeklySendsCount || 0,
        totalWeeklyReplies: weeklyRepliesData?.length || 0,
        totalWeeklyHumanReplies: weeklyHumanReplies?.length || 0,
        totalMTDSends: mtdSendsCount || 0,
        totalMTDReplies: mtdRepliesCount || 0,
        totalMTDPositive: mtdPositiveCount || 0,
        totalMTDCategorized: mtdCategorizedCount || 0,
        totalMTDMeetings: mtdMeetingsCount || 0,
        mtdUniqueSends: uniqueSends,
        mtdUniqueThreads: uniqueThreads,
        mtdFollowUpReplies: followUpReplies,
        sendsCountMethod: 'Using count query (no limit)',
        repliesLimitWarning: (weeklyRepliesData?.length || 0) >= 10000 ? '⚠️ Replies may be limited to 10,000' : '✅ Replies within limit'
      });

      // Get recent activity
      const recentSends = sendsData
        ?.slice(0, 10) // Get most recent 10
        .map(s => ({
          id: s.id,
          type: 'send' as const,
          timestamp: s.created_at,
          email: s.to_email,
          subject: s.campaign_name || 'Campaign Email',
          campaignName: s.campaign_name
        })) || [];

      const recentReplies = repliesData
        ?.slice(0, 10) // Get most recent 10
        .map(r => ({
          id: r.id,
          type: 'reply' as const,
          timestamp: r.time_replied || r.created_at,
          email: r.from_email || 'Unknown',
          subject: r.campaign_name || 'Email Reply',
          category: r.response_category,
          sentiment: r.reply_category_type
        })) || [];

      const allRecentActivity = [...recentSends, ...recentReplies]
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 20);

        const newMetrics = {
        totalSends,
        uniqueSends,
        sendsToday,
          sendsYesterday,
        sendsThisWeek,
        totalReplies,
          humanReplies,
          uniqueReplies: uniqueThreads, // Use uniqueThreads for Weekly Activity
        repliesToday,
          repliesYesterday,
        repliesThisWeek,
        followUpReplies,
        categorizedReplies,
        positiveReplies,
          positiveRepliesToday,
          positiveRepliesYesterday,
        negativeReplies,
        neutralReplies,
        meetingsBooked,
        meetingsToday,
          meetingsYesterday,
        lastUpdate: new Date().toISOString(),
        isLive: true
        };

        console.log('🔍 [LiveFeedDashboard] Setting new metrics:', newMetrics);
        setMetrics(newMetrics);

      setRecentActivity(allRecentActivity);

      console.log('✅ [LiveFeedDashboard] Metrics fetch completed successfully');

    } catch (error) {
      console.error('❌ [LiveFeedDashboard] Error fetching live feed metrics:', error);
      toast({
        title: "Error",
        description: "Failed to fetch live feed data. Please try refreshing.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    
    // Set up real-time subscription for live updates
    const channelName = `live-feed-${Math.random().toString(36).substr(2, 9)}`;
    console.log(`🔗 [LiveFeedDashboard] Setting up real-time subscription: ${channelName}`);
    
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'obsync_dynamic_reports' },
        (payload) => {
          console.log('🔴 [LiveFeedDashboard] New email event received:', payload.new);
          const newEvent = payload.new as any;
          
          // Only refresh for email events
          if (['EMAIL_SENT', 'EMAIL_REPLY', 'EMAIL_BOUNCE'].includes(newEvent.event_type)) {
            console.log('🔄 [LiveFeedDashboard] Refreshing data after new event...');
            setTimeout(() => fetchMetrics(), 1000); // Small delay to ensure data is committed
          } else {
            console.log('⏭️ [LiveFeedDashboard] Skipping non-email event:', newEvent.event_type);
          }
        }
      )
      .subscribe((status) => {
        console.log(`📡 [LiveFeedDashboard] Subscription status:`, status);
      });

    // Set up polling as backup
    let interval: NodeJS.Timeout | null = null;
    if (autoRefresh) {
      interval = setInterval(fetchMetrics, 30000); // Refresh every 30 seconds
    }

    // Set up Monday refresh check
    const checkForMondayRefresh = () => {
      const now = new Date();
      const utcNow = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
      const dayOfWeek = utcNow.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const hour = utcNow.getHours();
      const dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek];
      
      console.log(`📅 [LiveFeedDashboard] Checking for Monday refresh - Current: ${dayName} ${hour}:00 UTC`);
      
      // Check if it's Monday (1) and between 6 AM and 7 AM UTC to avoid multiple refreshes
      if (dayOfWeek === 1 && hour >= 6 && hour < 7) {
        console.log('📅 [LiveFeedDashboard] Monday detected - refreshing weekly metrics for new week');
        setLastMondayRefresh(utcNow.toISOString());
        fetchMetrics();
      }
    };

    // Check for Monday refresh every hour
    const mondayCheckInterval = setInterval(checkForMondayRefresh, 60 * 60 * 1000); // Every hour
    
    // Also check immediately in case the page loads on a Monday
    checkForMondayRefresh();

    return () => {
      console.log('🧹 [LiveFeedDashboard] Cleaning up subscription and intervals');
      supabase.removeChannel(channel);
      if (interval) clearInterval(interval);
      if (mondayCheckInterval) clearInterval(mondayCheckInterval);
    };
  }, [autoRefresh]);

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Show detail view if requested
  console.log('🔍 LiveFeedDashboard - showEmailSendsDetail:', showEmailSendsDetail)
  
  if (showEmailSendsDetail) {
    console.log('✅ Rendering EmailSendsDetailView')
    return <EmailSendsDetailView onBack={() => setShowEmailSendsDetail(false)} />
  }

  return (
    <div className="space-y-6">
      {/* Live Feed Controls */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Live Feed Dashboard</h2>
          <p className="text-muted-foreground">
            Real-time tracking of email sends, replies, and engagement metrics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={metrics.isLive ? "default" : "secondary"} className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${metrics.isLive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
            {metrics.isLive ? 'Live' : 'Offline'}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchMetrics}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh Now
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Email Sends */}
        <Card 
          className="cursor-pointer hover:border-primary transition-colors"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            console.log('📧 Email Sends card clicked!')
            console.log('📧 Current state:', showEmailSendsDetail)
            setShowEmailSendsDetail(true)
            console.log('📧 Setting state to true')
          }}
          style={{ cursor: 'pointer' }}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Email Sends</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalSends.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.uniqueSends.toLocaleString()} unique sends
            </p>
            <div className="flex items-center pt-1">
              <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
              <span className="text-xs text-green-500">
                {metrics.sendsToday} today
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Email Replies */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Email Replies</CardTitle>
            <Reply className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalReplies.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.humanReplies.toLocaleString()} human replies
            </p>
            <div className="flex items-center pt-1">
              <MessageSquare className="h-3 w-3 text-blue-500 mr-1" />
              <span className="text-xs text-blue-500">
                {metrics.followUpReplies} follow-ups
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Positive Replies */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Positive Replies</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.positiveReplies.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.categorizedReplies > 0 
                ? `${((metrics.positiveReplies / metrics.categorizedReplies) * 100).toFixed(1)}% of categorized`
                : 'No categorization data'
              }
            </p>
            <div className="flex items-center pt-1">
              <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
              <span className="text-xs text-green-500">
                {metrics.positiveRepliesToday || 0} replies today
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Meetings Booked */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Meetings Booked</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.meetingsBooked.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Total meetings scheduled
            </p>
            <div className="flex items-center pt-1">
              <Calendar className="h-3 w-3 text-purple-500 mr-1" />
              <span className="text-xs text-purple-500">
                {metrics.meetingsToday} today
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Performing Campaigns */}
      <EmailSendsTracker />

      {/* Detailed Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Categorization Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Reply Categorization (Weekly)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Total Categorized</span>
              <Badge variant="outline">{metrics.categorizedReplies}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-green-600">Positive</span>
              <Badge variant="outline" className="text-green-600">{metrics.positiveReplies}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-red-600">Negative</span>
              <Badge variant="outline" className="text-red-600">{metrics.negativeReplies}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Neutral</span>
              <Badge variant="outline" className="text-gray-600">{metrics.neutralReplies}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-purple-600">Meetings</span>
              <Badge variant="outline" className="text-purple-600">{metrics.meetingsBooked}</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Weekly Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Weekly Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Sends This Week</span>
              <Badge variant="outline">{metrics.sendsThisWeek}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Replies This Week</span>
              <Badge variant="outline">{metrics.repliesThisWeek}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Follow-up Replies</span>
              <Badge variant="outline">{metrics.followUpReplies}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Unique Threads</span>
              <Badge variant="outline">{metrics.uniqueReplies}</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {recentActivity.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No recent activity found
              </div>
            ) : (
              recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {activity.type === 'send' ? (
                      <Mail className="h-4 w-4 text-blue-500" />
                    ) : (
                      <Reply className="h-4 w-4 text-green-500" />
                    )}
                    <div>
                      <div className="font-medium text-sm">
                        {activity.type === 'send' ? 'Email Sent' : 'Reply Received'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {activity.email} • {activity.subject}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">
                      {formatTime(activity.timestamp)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatDate(activity.timestamp)}
                    </div>
                    {activity.category && (
                      <Badge variant="outline" className="text-xs mt-1">
                        {activity.category}
                      </Badge>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>


      {/* Last Update */}
      <div className="text-center text-sm text-muted-foreground">
        Last updated: {formatTime(metrics.lastUpdate)} • 
        Auto-refresh: {autoRefresh ? 'ON' : 'OFF'} • 
        {lastMondayRefresh && (
          <>Monday refresh: {formatTime(lastMondayRefresh)} • </>
        )}
        <Button
          variant="link"
          size="sm"
          onClick={() => setAutoRefresh(!autoRefresh)}
          className="p-0 h-auto ml-1"
        >
          {autoRefresh ? 'Disable' : 'Enable'}
        </Button>
      </div>
    </div>
  );
}
